import { ChevronDownIcon } from "@heroicons/react/24/outline";
import {
	type KeyboardEvent,
	type ReactNode,
	useCallback,
	useEffect,
	useId,
	useRef,
	useState,
} from "react";
import styles from "./Select.module.css";

export interface SelectOption {
	value: string;
	label: string;
	/** Optional secondary line (e.g. "Antagonist" under "Cetrotide"). */
	hint?: string;
}

interface SelectProps {
	id: string;
	value: string | null;
	options: readonly SelectOption[];
	placeholder: string;
	onChange: (value: string) => void;
	disabled?: boolean;
	/** Optional leading adornment (e.g. avatar, icon) rendered inside the trigger. */
	leading?: ReactNode;
	/** Optional aria-labelledby (defaults to FormField label via id). */
	ariaLabelledBy?: string;
}

/**
 * Phase 37 — Custom dropdown replacing the native `<select>` element.
 *
 * Rationale: native dropdowns render with OS-default option sizing which appears
 * miniature on mobile webviews and Capacitor. This component renders a styled
 * trigger button + popover listbox so options match the form typography
 * (15 px / Urbanist) and the dropdown matches Figma.
 *
 * a11y: trigger uses the WAI-ARIA combobox + listbox pattern — `role="combobox"`,
 * `aria-haspopup="listbox"`, `aria-controls` pointing at the listbox, and
 * `aria-activedescendant` pointing at the active option's id when open. The
 * listbox renders `role="listbox"` with options as `role="option"`. Focus stays
 * on the trigger so keyboard users navigate without focus jumps; SR readers
 * follow the active option via aria-activedescendant.
 *
 * Keyboard (handled on trigger): ArrowDown/ArrowUp navigate (opens panel if
 * closed), Enter/Space toggle (or commit when open), Escape closes, Tab closes.
 * Click outside closes via document-level pointerdown listener.
 */
export function Select({
	id,
	value,
	options,
	placeholder,
	onChange,
	disabled,
	leading,
	ariaLabelledBy,
}: SelectProps) {
	const [open, setOpen] = useState(false);
	const [activeIndex, setActiveIndex] = useState<number>(() => {
		const idx = options.findIndex((o) => o.value === value);
		return idx >= 0 ? idx : 0;
	});
	const rootRef = useRef<HTMLDivElement>(null);
	const listboxId = useId();
	const optionIdPrefix = `${listboxId}-opt`;

	const selectedOption = options.find((o) => o.value === value) ?? null;

	const close = useCallback(() => setOpen(false), []);

	// Keep `activeIndex` aligned with the current selection and bounded by the
	// options list. Without this, edit-mode prefill (parent passes a new
	// `value`) or async option loads leave the highlighted row pointing at the
	// wrong/out-of-range entry.
	useEffect(() => {
		if (options.length === 0) {
			setActiveIndex(0);
			return;
		}
		const selectedIdx = options.findIndex((o) => o.value === value);
		setActiveIndex((current) => {
			if (selectedIdx >= 0) return selectedIdx;
			if (current >= options.length) return options.length - 1;
			return current;
		});
	}, [value, options]);

	useEffect(() => {
		if (!open) return;
		const onPointer = (e: PointerEvent) => {
			if (rootRef.current && !rootRef.current.contains(e.target as Node)) {
				close();
			}
		};
		const onKey = (e: globalThis.KeyboardEvent) => {
			if (e.key === "Escape") close();
		};
		document.addEventListener("pointerdown", onPointer);
		document.addEventListener("keydown", onKey);
		return () => {
			document.removeEventListener("pointerdown", onPointer);
			document.removeEventListener("keydown", onKey);
		};
	}, [open, close]);

	const handleTriggerKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
		if (disabled) return;
		if (e.key === "ArrowDown") {
			e.preventDefault();
			if (!open) {
				setOpen(true);
				return;
			}
			setActiveIndex((i) =>
				options.length > 0 ? (i + 1) % options.length : 0,
			);
		} else if (e.key === "ArrowUp") {
			e.preventDefault();
			if (!open) {
				setOpen(true);
				return;
			}
			setActiveIndex((i) =>
				options.length > 0 ? (i - 1 + options.length) % options.length : 0,
			);
		} else if (e.key === "Enter" || e.key === " ") {
			e.preventDefault();
			if (!open) {
				setOpen((o) => !o);
				return;
			}
			const opt = options[activeIndex];
			if (opt) {
				onChange(opt.value);
				close();
			}
		} else if (e.key === "Tab" && open) {
			close();
		}
	};

	const onSelect = (opt: SelectOption) => {
		onChange(opt.value);
		close();
	};

	const activeOptionId =
		open && options[activeIndex]
			? `${optionIdPrefix}-${activeIndex}`
			: undefined;

	return (
		<div className={styles.root} ref={rootRef}>
			<button
				id={id}
				type="button"
				className={styles.trigger}
				onClick={() => !disabled && setOpen((o) => !o)}
				onKeyDown={handleTriggerKeyDown}
				role="combobox"
				aria-haspopup="listbox"
				aria-expanded={open}
				aria-controls={listboxId}
				aria-activedescendant={activeOptionId}
				aria-labelledby={ariaLabelledBy}
				disabled={disabled}
			>
				{leading ? <span className={styles.leading}>{leading}</span> : null}
				<span
					className={`${styles.value} ${selectedOption ? "" : styles.placeholder}`}
				>
					{selectedOption ? selectedOption.label : placeholder}
				</span>
				<ChevronDownIcon
					className={`${styles.chevron} ${open ? styles.chevronOpen : ""}`}
					aria-hidden="true"
				/>
			</button>
			{open ? (
				<div className={styles.listbox} role="listbox" id={listboxId}>
					{options.map((opt, idx) => {
						const isSelected = opt.value === value;
						const isActive = idx === activeIndex;
						return (
							<button
								key={opt.value}
								id={`${optionIdPrefix}-${idx}`}
								type="button"
								role="option"
								aria-selected={isSelected}
								className={`${styles.option} ${isActive ? styles.optionActive : ""} ${isSelected ? styles.optionSelected : ""}`}
								onClick={() => onSelect(opt)}
								onMouseEnter={() => setActiveIndex(idx)}
							>
								<span className={styles.optionLabel}>{opt.label}</span>
								{opt.hint ? (
									<span className={styles.optionHint}>{opt.hint}</span>
								) : null}
							</button>
						);
					})}
				</div>
			) : null}
		</div>
	);
}
